import { createAsyncThunk } from "@reduxjs/toolkit";
import { AdmPlan } from "@custom-types/base-types";
import { RootState } from "@store/store-helper";
import { fetchPlans } from "@store/plans/plans-thunks";
import { attachedTokenPlansSelector } from "@store/plans/plans-selector";
import { startPlanCopy } from "@store/plan-creation/plan-creation-slice";
import {
  canCopyPlan,
  findProductOfPlan,
  getCopyableProducts,
  getDefaultCopyStartDate,
  getRemainingPlanDates,
} from "@utils/plan-copy-utils";

interface PrefillPlanCopyProps {
  /** The ID of the plan that is copied */
  planId: AdmPlan["id"];
}

/**
 * Prefills the plan creation with the plan that is copied. The plans of the subject are fetched
 * first, so that the copy also works when the page is opened directly.
 * The plan subject and the products have to be in the store already, as the copy is made for that
 * subject from the product of the plan.
 */
export const prefillPlanCopy = createAsyncThunk<
  void,
  PrefillPlanCopyProps,
  { state: RootState }
>(
  "planCreation/prefillPlanCopy",
  async ({ planId }, { dispatch, getState }) => {
    const { planSubjectId, planSubjectType } =
      getState().planCreation.planSubject;

    if (!planSubjectId || !planSubjectType) {
      throw new Error("The subject of the copy is not known yet.");
    }

    await dispatch(
      fetchPlans({ subjectType: planSubjectType, subjectId: planSubjectId })
    ).unwrap();

    const state = getState();
    const plan = state.plans.entities[planId];

    if (!plan || plan.subject.id !== planSubjectId || !canCopyPlan(plan)) {
      throw new Error("This plan cannot be copied.");
    }

    const { custom, trial, bundle } = state.planCreation.products;
    const product = findProductOfPlan(
      plan,
      getCopyableProducts({
        custom: Object.values(custom ?? {}),
        trial: Object.values(trial ?? {}),
        bundle: Object.values(bundle ?? {}),
      })
    );

    if (!product || !plan.endDate) {
      throw new Error("The product of this plan is not available anymore.");
    }

    // Every year of the plan grants the same tokens, so the first one tells how many
    const [firstTokenPlan] = attachedTokenPlansSelector(planId)(state);
    const tokensPerYear =
      firstTokenPlan?.constraints.find(({ type }) => type === "CREDITS")?.data
        .max ?? 0;

    dispatch(
      startPlanCopy({
        plan,
        product,
        tokensPerYear,
        startDate: getDefaultCopyStartDate({ copiedPlan: plan }),
        remainingDates: getRemainingPlanDates({ plan }),
      })
    );
  }
);
